import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getUserSubscriptionHref,
  userSubscriptionColumns,
  userSubscriptionRow,
} from "./tableParams/userSubscriptionColumns";
import {
  CANCEL_USER_SUBSCRIPTION,
  GET_USER_SUBSCRIPTIONS,
} from "../../../actions/ActionTypes";
import { fetchApi } from "../../../actions/FetchActions";
import { postApi } from "../../../actions/SendActions";
import ProjectAPI from "../../../api/ProjectAPI";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

import type { JSX } from "react";

import type { UserSubscriptionRow } from "./tableParams/userSubscriptionColumns.types";
import type { AppState } from "../../../reducers";

interface SubscribersTableProps {
  userUuid: string;
  offerAbility: boolean;
}

function SubscribersTable({
  userUuid,
  offerAbility,
}: SubscribersTableProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const dispatch = useDispatch();
  const store = useStore<AppState>();

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const offers = useSelector((state: AppState) => state.fetchReducer.offers);
  // Sélecteurs séparés : le reducer d'arrêt garde la même référence pour l'objet entier.
  const subscriptions = useSelector(
    (state: AppState) => state.fetchReducer.user_subscriptions.data,
  );
  const isFetched = useSelector(
    (state: AppState) => state.fetchReducer.user_subscriptions.isFetched,
  );

  // Suivi local : la requête d'arrêt passe par le même item Redux et fait varier `isFetching` et `failed`.
  const [isReloading, setIsReloading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [pendingUuids, setPendingUuids] = useState<string[]>([]);

  const getPlatformsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getPlatforms,
    params: [{ projectSlug }],
  });
  const { data: platformData } = useQuery(getPlatformsQuery);
  const hasPaypalProvider =
    platformData?.providers.some(
      (offerPlanProvider) => offerPlanProvider.payment_provider === "paypal",
    ) ?? false;

  const fetchSubscriptions = useCallback((): void => {
    setIsReloading(true);
    setLoadFailed(false);
    Promise.resolve(
      dispatch(
        fetchApi({
          endpoint: `/api/editor_stats/v1/admin/user/subscriptions?user_uuid=${userUuid}&period_start=1970-01-01`,
          type: GET_USER_SUBSCRIPTIONS,
          item: "user_subscriptions",
          headers: [{ key: "xx-ok-project", value: projectSlug }],
          token,
        }),
      ),
    ).then(() => {
      setLoadFailed(store.getState().fetchReducer.user_subscriptions.failed);
      setIsReloading(false);
    });
  }, [dispatch, store, projectSlug, token, userUuid]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const cancelSubscription = useCallback(
    (subscriptionUuid: string, cancelAtPeriodEnd: boolean): void => {
      const stopPending = (): void =>
        setPendingUuids((current) =>
          current.filter((uuid) => uuid !== subscriptionUuid),
        );

      setPendingUuids((current) => [...current, subscriptionUuid]);

      dispatch(
        postApi({
          endpoint: `/api/offer/v5/admin/subscriptions/${subscriptionUuid}?user_uuid=${userUuid}&cancel_at_period_end=${cancelAtPeriodEnd}`,
          type: CANCEL_USER_SUBSCRIPTION,
          item: "user_subscriptions",
          headers: [{ key: "xx-ok-project", value: projectSlug }],
          token,
          callback: stopPending,
          callbackError: () => {
            stopPending();
            toast.error(t("error:genericError"));
          },
        }),
      );
    },
    [dispatch, projectSlug, token, userUuid, t],
  );

  const rowList = useMemo(
    (): UserSubscriptionRow[] =>
      // Le reducer garde les abonnements d'un utilisateur précédent sur un échec.
      loadFailed
        ? []
        : subscriptions.map((subscription) =>
            userSubscriptionRow(subscription, offers, t),
          ),
    [subscriptions, offers, t, loadFailed],
  );

  const columnList = useMemo(
    () =>
      userSubscriptionColumns({
        t,
        projectSlug,
        offerAbility,
        hasPaypalProvider,
        pendingUuids,
        onCancel: cancelSubscription,
      }),
    [
      t,
      projectSlug,
      offerAbility,
      hasPaypalProvider,
      pendingUuids,
      cancelSubscription,
    ],
  );

  const getRowHref = useCallback(
    (row: UserSubscriptionRow): string | undefined =>
      getUserSubscriptionHref(row, projectSlug, offerAbility),
    [projectSlug, offerAbility],
  );

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("subscriptionsTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "subscription_date", desc: true }}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          getRowId={(row) => row.subscription_uuid}
          isError={loadFailed}
          isLoading={isReloading || !isFetched}
          onRetry={fetchSubscriptions}
          urlSync={{ prefix: "subscriptions" }}
        />
      </CardContent>
    </Card>
  );
}

export default SubscribersTable;
